import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  filter,
  finalize,
  Observable,
  switchMap,
  take,
  tap,
  throwError,
  timeout,
} from 'rxjs';
import { AuthService, RefreshTokenData } from './auth.service';
import { Router } from '@angular/router';
import { ResponseModel } from './model/auth.model';
import { TokenStorageService } from './token-storage.service';
import { LoadingIndicatorService } from './loading-indicator.service';
import { ToastControllerService } from 'src/app/pages/services/toast-controller.service';
import { storage } from 'src/environments/environment';

// Upper bound on how long a single refresh may take before it is treated as a
// transient failure, so a stalled-but-subscribed refresh can't hang indefinitely.
// Set above the backend's realistic worst-case refresh latency: too low and a
// slow-but-successful rotation is discarded while the server has already rotated
// and blacklisted the old token (an orphaned rotation that forces a logout on the
// next request). The durable fix for that race is a backend reuse grace window or
// idempotent rotation; this bound only shrinks the window.
const REFRESH_TIMEOUT_MS = 30000;

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  // Single-flight refresh guard: one refresh runs at a time; concurrent 401s wait
  // for it and replay with the new token. Required under BLACKLIST_AFTER_ROTATION,
  // where a second concurrent refresh would present an already-blacklisted token
  // and bounce the user to login. A fresh subject is created per refresh so a
  // failed refresh can release waiters via error() without poisoning later ones.
  private isRefreshing = false;
  // Emits once the in-flight refresh finishes (true), or errors if it fails.
  // Waiters re-read the refreshed token from storage, so only the completion
  // signal matters here, not the token value.
  private refreshComplete$ = new BehaviorSubject<boolean>(false);

  constructor(
    private authService: AuthService,

    private router: Router,
    private tokenStorageService: TokenStorageService,
    private loadingService: LoadingIndicatorService,
    private toast: ToastControllerService
  ) {}

  intercept<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    // The access token this request is leaving with. If a 401 comes back after a
    // concurrent refresh has already rotated the token in storage, handle401 uses
    // this to replay directly rather than starting a redundant refresh.
    const sentAccess = this.tokenStorageService.getAccessToken();
    // The login this request belongs to. Captured here so a delayed 401 can tell
    // "the same login rotated its token" apart from "a different account signed in
    // (or the user logged out) while this request was in flight". Without it, a
    // replay would resend this request's body under whatever credentials and tenant
    // header storage now holds — potentially another account's.
    const sentSession = this.tokenStorageService.getLoginSession();
    return next.handle(this.addAuthToken(request)).pipe(
      tap(() => {
        if (request.method === 'POST' || request.method === 'PUT') {
          this.loadingService.show();
        }
      }),
      catchError((err: HttpErrorResponse) => {
        // Every 401 is a refresh candidate, including SimpleJWT's token_not_valid,
        // which is exactly what a request carrying an expired access token returns.
        // handle401 refreshes once and replays; it only redirects to login when the
        // refresh itself shows the session is genuinely dead.
        //
        // Logout is the exception: it is fire-and-forget and the caller has already
        // cleared storage and navigated to /login. A 401 here (expired access token
        // at logout time) must not kick off a refresh against cleared storage, which
        // would otherwise force a second, redundant /login navigation.
        const isLogout = request.url.endsWith('/logout');
        if (err.status === 401 && !isLogout) {
          return this.handle401(request, next, sentAccess, sentSession);
        }
        return throwError(() => err);
      }),
      finalize(() => {
        this.loadingService.hide();
      })
    );
  }

  // Refresh once, replay the failed request with the new token. Concurrent 401s
  // that arrive while a refresh is in flight wait for the shared result and then
  // replay, so only one refresh (and one rotation) ever happens per burst. The
  // refresh itself runs on its own subscription (startRefresh), decoupled from any
  // request, so the request that happened to trigger it being cancelled — a route
  // change, a UserComponent search switching, the page closing — does not cancel
  // the refresh: it still saves the rotated tokens and releases the other waiters.
  private handle401<T>(
    request: HttpRequest<T>,
    next: HttpHandler,
    sentAccess: string | null,
    sentSession: string | null
  ): Observable<HttpEvent<T>> {
    // Bail before any replay if the login that issued this request has ended or
    // changed (logout, or a different account signed in) while it was in flight.
    // Both replay paths below re-attach the *currently stored* token and tenant
    // header via addAuthToken, so replaying here would submit this request's body
    // under another account's credentials. Fail it instead. (The detached refresh
    // path guards the same race separately via loginEndedOrChanged.)
    if (this.tokenStorageService.getLoginSession() !== sentSession) {
      return throwError(() => new Error('Login ended or changed before replay'));
    }

    // A 401 can arrive after a concurrent refresh has already finished: the request
    // left with the old token, but storage now holds a newer one. With isRefreshing
    // already back to false, the block below would start a second, redundant refresh
    // (and spend the refresh token again under rotation). Instead, replay straight
    // away with the token already saved.
    const currentAccess = this.tokenStorageService.getAccessToken();
    if (!this.isRefreshing && sentAccess && currentAccess && currentAccess !== sentAccess) {
      return this.replay(request, next);
    }

    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.startRefresh();
    }

    // Leader and waiters alike wait for the shared refresh result, then replay with
    // the now-current token. The timeout is a backstop: the refresh is already
    // bounded, so this only guards against a completion signal that never arrives.
    return this.refreshComplete$.pipe(
      filter((done) => done),
      take(1),
      timeout(REFRESH_TIMEOUT_MS),
      switchMap(() => this.replay(request, next))
    );
  }

  // Run the single shared refresh on a standalone subscription, decoupled from the
  // request lifecycle, and drive refreshComplete$ with the outcome. Because nothing
  // here is tied to a request's subscription, a cancelled request can never abort
  // the refresh, orphan a rotation, or strand the other waiters.
  private startRefresh(): void {
    // The refresh token we are about to spend. If the refresh fails but storage now
    // holds a different token *within the same login*, another tab/instance refreshed
    // first — we then release waiters to replay with its token instead of logging out.
    const sentRefresh = this.tokenStorageService.getRefreshToken();
    // Identity of the login that started this refresh. The refresh is detached from
    // any request, so it can resolve after the user has logged out or signed into a
    // different account; the session id changes on login/logout but not on rotation,
    // letting us tell those apart (see loginEndedOrChanged).
    const sentSession = this.tokenStorageService.getLoginSession();
    const done$ = (this.refreshComplete$ = new BehaviorSubject<boolean>(false));

    // Has the login that began this refresh ended or changed while it was in flight?
    // True if a different account signed in (new session id) or the user logged out
    // (session id cleared, or the refresh token we started with was wiped from
    // storage). When this holds, the waiting requests carry the *old* login's bodies,
    // so they must be failed rather than replayed with the new account's token and
    // tenant header — and the new/absent session must not be logged out on their
    // behalf. The sentRefresh guard keeps a genuine "no token to begin with" dead
    // session (sentRefresh already null) out of this branch so it still logs out.
    const loginEndedOrChanged = (): boolean =>
      this.tokenStorageService.getLoginSession() !== sentSession ||
      (sentRefresh !== null && this.tokenStorageService.getRefreshToken() === null);

    this.authService
      .refresh()
      .pipe(timeout(REFRESH_TIMEOUT_MS))
      .subscribe({
        next: (res: ResponseModel<RefreshTokenData>) => {
          this.isRefreshing = false;

          if (loginEndedOrChanged()) {
            // Our rotated tokens belong to a login that no longer exists: discard
            // them (saving would clobber the current account or resurrect a logged-
            // out one) and fail the stale waiters rather than replay them as the new
            // account.
            done$.error(new Error('Login ended or changed during refresh'));
            return;
          }

          const access = res?.data?.access;
          if (!access) {
            // A 2xx with no access token is a broken, unusable session, not a
            // transient glitch — force a clean re-auth rather than retrying blind.
            done$.error(new Error('Malformed refresh response'));
            this.forceLogout();
            return;
          }
          this.tokenStorageService.saveTokens(access, res.data.refresh);
          done$.next(true);
        },
        error: (refreshErr) => {
          this.isRefreshing = false;

          if (loginEndedOrChanged()) {
            // Same as the success path: the login changed or ended mid-flight. Fail
            // the stale waiters without replaying them as the new account and without
            // logging the new/absent session out.
            done$.error(refreshErr);
            return;
          }

          const currentRefresh = this.tokenStorageService.getRefreshToken();
          if (currentRefresh !== sentRefresh) {
            // Still the same login, but another tab/instance rotated the token while
            // ours was in flight. Release waiters to replay with the newer token
            // instead of logging out an otherwise-live session.
            done$.next(true);
            return;
          }

          done$.error(refreshErr); // release queued waiters as failed
          if (this.isDeadSession(refreshErr)) {
            // Session is genuinely dead (no refresh token, or the endpoint rejected
            // it with a 400): send the user to log in.
            this.forceLogout();
          } else {
            // Transient failure (5xx / network / timeout): keep the session but
            // surface it, so the user isn't trapped in a silent, feedback-less
            // broken state while the app still believes it is logged in (OM-562).
            this.toast.warningToastVisible('Connection problem, please try again.');
          }
        },
      });
  }

  // Replay a request after a refresh, re-reading the now-current access token. If
  // the freshly refreshed token is itself rejected with a 401 (clock skew, or a
  // token already invalid), the session really is dead — force re-auth rather than
  // letting the request fail silently while the app still looks logged in.
  private replay<T>(request: HttpRequest<T>, next: HttpHandler): Observable<HttpEvent<T>> {
    return next.handle(this.addAuthToken(request)).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          this.forceLogout();
        }
        return throwError(() => err);
      })
    );
  }

  private forceLogout(): void {
    this.router.navigate(['/login']);
  }

  // A refresh failure means "log out" only when the session is genuinely dead:
  // there was no refresh token to send, or the refresh endpoint rejected the token.
  private isDeadSession(err: unknown): boolean {
    // Read storage directly rather than matching the error message string, so this
    // can't silently drift from whatever AuthService.refresh() throws.
    if (!this.tokenStorageService.getRefreshToken()) {
      return true;
    }
    // The backend returns 400 for an invalid/expired/blacklisted refresh token —
    // the one unambiguous dead-session signal. A 401/403/5xx or a network error
    // from the refresh path is infra/transient and must not force a logout.
    if (err instanceof HttpErrorResponse) {
      return err.status === 400;
    }
    return false;
  }
  addAuthToken<T>(request: HttpRequest<T>): HttpRequest<T> {
    const token = this.tokenStorageService.getAccessToken();

    if (!token) {
      return request;
    }

    const excludedParts = [
      'tenants',
      'logout',
      'user',
      'role',
      'user-details',
      'change-password',
      'change-email',
      'profile-picture',
    ];

    const isExcluded = excludedParts.some(
      (part) => request.url.endsWith(`/${part}`) || request.url.includes(`/${part}/`)
    );

    // Decode token or fallback to localStorage for tenant scoping
    let activeTenantId: string | null = null;
    try {
      const decoded = this.tokenStorageService.decodeAccessToken() as { tenant_id?: string } | null;
      activeTenantId = decoded?.tenant_id || localStorage.getItem(storage.tenant_id);
    } catch (error) {
      console.error('Error decoding token:', error);
      activeTenantId = localStorage.getItem(storage.tenant_id);
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
    };

    if (!isExcluded && activeTenantId) {
      headers['x-tenant-id'] = activeTenantId;
    }

    return request.clone({
      setHeaders: headers,
    });
  }
}
